'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import styles from './reviews-marquee.module.css';

// Only the motion control hydrates. Review cards and sanitized text are server children.
export function ReviewsMarquee({ children }: { children: ReactNode }) {
  const { t } = useTranslations();
  const root = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const element = root.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let visible = false;
    const update = () => {
      element.dataset.motion = preference.matches ? 'static' : 'enabled';
      element.dataset.visible = String(visible && !document.hidden);
    };
    const observer = new IntersectionObserver(entries => {
      visible = entries.some(entry => entry.isIntersecting);
      update();
    });
    observer.observe(element);
    preference.addEventListener('change', update);
    document.addEventListener('visibilitychange', update);
    update();
    return () => {
      observer.disconnect();
      preference.removeEventListener('change', update);
      document.removeEventListener('visibilitychange', update);
    };
  }, []);

  return (
    <div ref={root} className={styles.root} data-paused={paused}>
      <div
        className={styles.viewport}
        tabIndex={0}
        role="region"
        aria-label={t('Recenzie zákazníkov')}
      >
        <div className={styles.track}>{children}</div>
      </div>
      <button
        type="button"
        className={styles.pause}
        aria-pressed={paused}
        onClick={() => setPaused(value => !value)}
      >
        {paused ? t('Spustiť posúvanie recenzií') : t('Pozastaviť posúvanie recenzií')}
      </button>
    </div>
  );
}
